import { LoadingDots } from '@expressable/syntax-ds';
import React from 'react';
import { useEffectOnce } from 'react-use';
import { useSignupDirectCtx } from '../context';
import { getJotformRegistrationFormLink } from '../misc/jotform/jotformRegistrationForm';
import { useTrack } from 'utils/hooks/useMixPanel';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from '../misc/mixpanelTracking';
import { useNavigate } from 'react-router-dom';
import { routes } from 'router/routes';
import { JotformRedirectReason } from '../misc/jotform/urls';
import { captureException } from '@sentry/react';
import { SignupError } from 'utils/errors/debuggableError';

/** Wait time in MS before redirecting. This is used to make sure all tracking event calls are ready by the time the app is unloaded
 *  We can wait on the redirect event directly but not on other events called on other components. This is a simple workaround
 *
 *  Other events examples: Clicked bailout event which triggers the redirect to jotform immediately
 */
const MANUAL_WAIT_TIME = 1000;

/** Simple loading page for redirect to jotform */
export const SignupJotformRedirect: React.FC = () => {
  const { trackAsync } = useTrack();

  const { filters, resumedLeadState, jotformRedirectReason } = useSignupDirectCtx();

  const navigate = useNavigate();

  const onStartRedirect = async () => {
    if (!jotformRedirectReason) {
      captureException(new SignupError('Jotform redirect reason is missing'));
    }
    const reason = jotformRedirectReason || JotformRedirectReason.AVAILABILITY_AUTOMATIC_BAILOUT;
    if (filters) {
      await trackAsync(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.REDIRECT_TO_JOTFORM.FULL_FORM);

      await new Promise(s => setTimeout(s, MANUAL_WAIT_TIME));
      const jotformURL = getJotformRegistrationFormLink({
        filters,
        leadId: resumedLeadState?.leadID,
        reason,
      });

      window.location.href = jotformURL;
    } else {
      navigate(routes.signupDirect.base);
    }
  };

  useEffectOnce(() => {
    onStartRedirect();
  });

  return <LoadingDots className="flex flex-col h-max static" />;
};
